import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Copy, Check, Download, Upload, Loader2, Package, Images, Trash2,
    RefreshCw, ClipboardList, IndianRupee, Users, ShieldCheck,
    ChevronLeft, ChevronRight, CalendarHeart, BookHeart, Send,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import client, { mediaUrl } from "@/lib/api";
import { imageFile, maxLen, firstError, MAX_PDF_MB } from "@/lib/validate";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";

const TABS = [
    { key: "queued", label: "To illustrate", icon: ClipboardList },
    { key: "revision", label: "Revisions", icon: RefreshCw },
    { key: "in_progress", label: "In progress", icon: Loader2 },
    { key: "delivered", label: "Delivered", icon: Check },
    { key: "payments", label: "Payments", icon: IndianRupee },
    { key: "plans", label: "Year Books", icon: CalendarHeart },
    { key: "users", label: "Customers", icon: Users },
    { key: "samples", label: "Samples", icon: Images },
];

// The four tabs backed by the book queue; the rest read other collections.
const BOOK_TABS = ["queued", "revision", "in_progress", "delivered"];

// Posted by hand, so fulfilment is a human clicking through these in order.
// Must match SHIPPING_STATES in server.py.
const SHIP_FLOW = ["processing", "printing", "packed", "shipped", "delivered"];

// Orders that need a human: a UPI credit to check, or money that went missing.
// A paid hardcover is also a job: nobody posts it unless someone sees it.
const needsAction = (o) =>
    o.status === "upi_claimed" || o.status === "duplicate"
    || (o.payment_issue && o.status !== "refunded")
    || (o.kind === "hardcover" && o.paid_at
        && !["delivered", "refunded", "cancelled"].includes(o.status));

export default function Admin() {
    const { user, ready } = useAuth();
    const nav = useNavigate();
    const [tab, setTab] = useState("queued");
    const [queue, setQueue] = useState([]);
    const [counts, setCounts] = useState({});
    const [stats, setStats] = useState(null);
    const [orders, setOrders] = useState([]);
    const [users, setUsers] = useState([]);
    const [plans, setPlans] = useState([]);
    const [planCounts, setPlanCounts] = useState({});
    const [loading, setLoading] = useState(true);
    // One page number, reset whenever the tab changes — each tab shows a
    // different list and they page independently of one another.
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({});

    useEffect(() => {
        if (ready && (!user || user.role !== "admin")) nav("/");
    }, [user, ready, nav]);

    // Only the visible list pages; the other three stay on page 1 so their tab
    // counts and the payments badge remain correct.
    const bookTab = BOOK_TABS.includes(tab);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const at = (t) => (tab === t ? page : 1);
            const [q, s, o, u, p] = await Promise.all([
                client.get("/admin/queue", {
                    params: { status: bookTab ? tab : undefined, page: bookTab ? page : 1 },
                }),
                client.get("/admin/stats").catch(() => ({ data: null })),
                client.get("/admin/orders", { params: { page: at("payments") } })
                    .catch(() => ({ data: { orders: [] } })),
                client.get("/admin/users", { params: { page: at("users") } })
                    .catch(() => ({ data: { users: [] } })),
                client.get("/admin/subscriptions", { params: { page: at("plans") } })
                    .catch(() => ({ data: { subscriptions: [] } })),
            ]);
            setQueue(q.data.queue || []);
            setCounts(q.data.counts || {});
            setStats(s.data);
            setOrders(o.data.orders || []);
            setUsers(u.data.users || []);
            setPlans(p.data.subscriptions || []);
            setPlanCounts(p.data.counts || {});
            setMeta({
                queue: { page: q.data.page, pages: q.data.pages, total: q.data.total },
                payments: { page: o.data.page, pages: o.data.pages, total: o.data.total },
                users: { page: u.data.page, pages: u.data.pages, total: u.data.total },
                plans: { page: p.data.page, pages: p.data.pages, total: p.data.total },
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load the queue");
        } finally { setLoading(false); }
    }, [tab, page, bookTab]);

    useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);
    // A page 4 on one tab means nothing on the next.
    useEffect(() => { setPage(1); }, [tab]);

    const shown = useMemo(() => queue.filter((b) => b.status === tab), [queue, tab]);
    // The badge counts what needs a human, not what exists: a plan quietly
    // collecting answers is not a job, a book that is now owed is.
    const tabCounts = useMemo(
        () => ({ ...counts, payments: orders.filter(needsAction).length,
                 plans: planCounts.book_due || 0 }),
        [counts, orders, planCounts]);

    if (!ready || user?.role !== "admin") return null;

    return (
        <div className="min-h-screen magic-bg">
            <Toaster position="top-center" richColors />
            <Navbar />
            <div className="max-w-6xl mx-auto px-6 py-10">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                    <div>
                        <div className="text-xs tracking-[0.28em] uppercase text-plum/60 mb-2">Studio</div>
                        <h1 className="font-display text-4xl tracking-tight">Books waiting to be made.</h1>
                        <div className="script-note text-2xl mt-1">the queue, in order</div>
                    </div>
                    <Button variant="outline" className="rounded-full" onClick={load} disabled={loading}
                        data-testid="admin-refresh-btn">
                        <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                </div>

                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
                        <Stat label="Revenue" value={`₹${(stats.revenue_inr || 0).toLocaleString("en-IN")}`} icon={IndianRupee} />
                        <Stat label="Orders" value={stats.orders} icon={Package} />
                        <Stat label="Families" value={stats.verified_users} icon={ClipboardList} />
                        <Stat label="Books made" value={stats.books_purchased} icon={Images} />
                    </div>
                )}

                <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
                    {TABS.map((t) => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            data-testid={`admin-tab-${t.key}`}
                            className={`rounded-full px-4 h-10 text-sm font-medium border transition-all ${
                                tab === t.key
                                    ? "bg-bubblegum text-white border-bubblegum"
                                    : "bg-white/70 text-plum border-plum/10 hover:bg-white"
                            }`}>
                            {t.label}
                            {t.key !== "samples" && tabCounts[t.key] > 0 && (
                                <span className={`ml-2 text-xs ${tab === t.key ? "text-white/80" : "text-plum/50"}`}>
                                    {tabCounts[t.key]}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {tab === "samples" ? (
                    <SamplesManager />
                ) : tab === "payments" ? (
                    <>
                        <PaymentsPanel orders={orders} onDone={load} />
                        <Pager meta={meta.payments} page={page} onPage={setPage} noun="orders" />
                    </>
                ) : tab === "plans" ? (
                    <>
                        <PlansPanel plans={plans} counts={planCounts} onDone={load} />
                        <Pager meta={meta.plans} page={page} onPage={setPage} noun="plans" />
                    </>
                ) : tab === "users" ? (
                    <>
                        <UsersPanel users={users} orders={orders} />
                        <Pager meta={meta.users} page={page} onPage={setPage} noun="customers" />
                    </>
                ) : loading ? (
                    <div className="card-cloud p-12 text-center text-plum/50">Loading…</div>
                ) : shown.length === 0 ? (
                    <div className="card-cloud p-12 text-center text-plum/60" data-testid="admin-empty">
                        Nothing here right now.
                    </div>
                ) : (
                    <>
                        <div className="space-y-6">
                            {shown.map((b) => <QueueCard key={b.id} book={b} onDone={load} />)}
                        </div>
                        <Pager meta={meta.queue} page={page} onPage={setPage} noun="books" />
                    </>
                )}
            </div>
        </div>
    );
}

// Who bought a Year Book plan, how far in they are, and whose book is now owed.
// "Make their book" is the end of the plan: it builds the book from what they
// actually wrote, already paid for, straight into the illustration queue.
function PlansPanel({ plans, counts, onDone }) {
    const [openId, setOpenId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState(null);
    const [childName, setChildName] = useState("");
    const [sweeping, setSweeping] = useState(false);

    const open = async (s) => {
        if (openId === s.id) { setOpenId(null); setDetail(null); return; }
        setOpenId(s.id); setDetail(null); setChildName("");
        try {
            const { data } = await client.get(`/admin/subscriptions/${s.id}/memories`);
            setDetail(data);
        } catch { toast.error("Could not open that family's memories"); }
    };

    const sweep = async () => {
        setSweeping(true);
        try {
            const { data } = await client.post("/admin/subscriptions/sweep");
            toast.success(data.issued ? `Sent ${data.issued} question${data.issued > 1 ? "s" : ""}`
                                      : "Nobody is due a question yet");
        } catch { toast.error("Could not send this week's questions"); }
        finally { setSweeping(false); }
    };

    const makeBook = async (s) => {
        setBusy(s.id);
        try {
            const { data } = await client.post(`/admin/subscriptions/${s.id}/make-book`,
                { child_name: childName.trim() || null });
            toast.success(`"${data.title}" is in the queue — ${data.memories} pages from their year.`);
            setOpenId(null); setDetail(null);
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not build their book");
        } finally { setBusy(null); }
    };

    return (
        <div data-testid="admin-plans">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div className="text-sm text-plum/60">
                    {counts.active || 0} collecting · {counts.book_due || 0} book{(counts.book_due || 0) === 1 ? "" : "s"} due
                </div>
                <Button variant="outline" className="rounded-full" onClick={sweep} disabled={sweeping}
                    data-testid="admin-sweep-btn">
                    <Send className={`w-4 h-4 mr-1 ${sweeping ? "animate-pulse" : ""}`} />
                    Send this week's questions
                </Button>
            </div>

            {plans.length === 0 ? (
                <div className="card-cloud p-8 text-center text-plum/60">
                    Nobody has started a Year Book yet.
                </div>
            ) : (
                <div className="grid gap-3">
                    {plans.map((s) => {
                        const due = s.book_status === "ready";
                        const made = s.book_status === "made";
                        const pct = Math.min(100, Math.round((s.answered / (s.weeks || 26)) * 100));
                        return (
                            <div key={s.id} className="card-cloud p-5" data-testid={`plan-${s.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-medium text-plum truncate">
                                            {s.customer?.name || s.customer?.email || s.customer?.phone || s.user_id}
                                        </div>
                                        <div className="text-xs text-plum/50 mt-0.5">
                                            {s.customer?.email || s.customer?.phone || ""}
                                            {s.expires_at ? ` · ends ${new Date(s.expires_at).toLocaleDateString()}` : ""}
                                        </div>
                                    </div>
                                    <div className={`rounded-full px-3 py-1 text-xs font-medium ${
                                        due ? "bg-bubblegum text-white"
                                            : made ? "bg-mint/40 text-plum" : "bg-lavender/50 text-plum/70"}`}>
                                        {due ? "Book due" : made ? "Book made" : "Collecting"}
                                    </div>
                                </div>

                                <div className="mt-3 flex items-center gap-3">
                                    <div className="flex-1 h-2 rounded-full bg-plum/10 overflow-hidden">
                                        <div className="h-full bg-bubblegum rounded-full" style={{ width: `${pct}%` }} />
                                    </div>
                                    <div className="text-xs text-plum/60 shrink-0">
                                        {s.answered}/{s.weeks} answered
                                        {s.unanswered > 0 ? ` · ${s.unanswered} waiting` : ""}
                                    </div>
                                </div>

                                <div className="mt-4 flex flex-wrap gap-2">
                                    <Button variant="outline" size="sm" className="rounded-full"
                                        onClick={() => open(s)} data-testid={`plan-open-${s.id}`}>
                                        <BookHeart className="w-4 h-4 mr-1" />
                                        {openId === s.id ? "Hide their year" : "See their year"}
                                    </Button>
                                    {!made && (
                                        <Button size="sm" onClick={() => makeBook(s)}
                                            disabled={busy === s.id || s.answered === 0}
                                            data-testid={`plan-make-${s.id}`}
                                            className="rounded-full bg-bubblegum hover:bg-bubblegum/90">
                                            {busy === s.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                                            Make their book
                                        </Button>
                                    )}
                                </div>

                                {openId === s.id && (
                                    <div className="mt-4 border-t border-plum/10 pt-4">
                                        {/* A plan customer may never have made a character, so the
                                            child's name has to be askable here. */}
                                        <Input value={childName} onChange={(e) => setChildName(e.target.value)}
                                            maxLength={60} className="bg-white max-w-xs mb-4"
                                            data-testid={`plan-child-${s.id}`}
                                            placeholder="Child's name (only if they saved no cast)" />
                                        {!detail ? (
                                            <div className="text-sm text-plum/50">Opening their year…</div>
                                        ) : detail.memories.length === 0 ? (
                                            <div className="text-sm text-plum/50">Nothing answered yet.</div>
                                        ) : (
                                            <ol className="grid gap-2">
                                                {detail.memories.map((m, i) => (
                                                    <li key={m.id} className="flex items-start gap-3 text-sm">
                                                        <span className="text-plum/35 shrink-0 w-14">
                                                            page {i + 2}
                                                        </span>
                                                        {m.photo_b64 && (
                                                            <img src={m.photo_b64} alt=""
                                                                className="w-10 h-10 rounded object-cover shrink-0" />
                                                        )}
                                                        <span className="min-w-0">
                                                            <span className="text-plum">{m.text}</span>
                                                            <span className="text-plum/40 text-xs block">
                                                                {[m.question_index != null && `week ${m.question_index + 1}`,
                                                                  m.told_by && `told by ${m.told_by}`]
                                                                    .filter(Boolean).join(" · ")}
                                                            </span>
                                                        </span>
                                                    </li>
                                                ))}
                                            </ol>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function UsersPanel({ users, orders }) {
    // Spend per account, so the list answers "who are my customers" rather than
    // just "who signed up".
    const spend = useMemo(() => {
        const m = {};
        for (const o of orders) {
            if (!o.paid_at || o.status === "refunded") continue;
            m[o.user_id] = (m[o.user_id] || 0) + o.amount;
        }
        return m;
    }, [orders]);

    if (!users.length) {
        return <div className="card-cloud p-12 text-center text-plum/60" data-testid="users-empty">
            No accounts yet.
        </div>;
    }
    return (
        <div className="card-cloud divide-y divide-plum/5 overflow-hidden" data-testid="users-list">
            {users.map((u) => (
                <div key={u.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 p-4"
                    data-testid={`user-${u.id}`}>
                    <div className="w-9 h-9 rounded-full bg-lavender/50 grid place-items-center shrink-0
                                    text-xs font-semibold text-plum uppercase">
                        {(u.name || u.email || u.phone || "?").slice(0, 1)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-plum truncate flex items-center gap-2">
                            {u.name || "No name"}
                            {u.role === "admin" && (
                                <span className="text-[10px] uppercase tracking-wider text-bubblegum
                                                 flex items-center gap-1">
                                    <ShieldCheck className="w-3 h-3" /> admin
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-plum/50 truncate">
                            {u.email || u.phone || "—"}
                            {u.created_at ? ` · joined ${new Date(u.created_at).toLocaleDateString()}` : ""}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-sm text-plum">
                            {spend[u.id] ? `₹${(spend[u.id] / 100).toFixed(0)}` : "—"}
                        </div>
                        <div className={`text-xs ${u.verified ? "text-plum/40" : "text-amber-600"}`}>
                            {u.verified ? "verified" : "unverified"}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function PaymentsPanel({ orders, onDone }) {
    // Anything waiting on a decision floats to the top; the rest is history.
    const sorted = useMemo(
        () => [...orders].sort((a, b) => needsAction(b) - needsAction(a)), [orders]);
    if (!sorted.length) {
        return <div className="card-cloud p-12 text-center text-plum/60" data-testid="payments-empty">
            No orders yet.
        </div>;
    }
    return (
        <div className="space-y-4" data-testid="payments-list">
            {sorted.map((o) => <OrderRow key={o.id} order={o} onDone={onDone} />)}
        </div>
    );
}

function OrderRow({ order, onDone }) {
    const [busy, setBusy] = useState(false);
    const act = async (path, body, ok) => {
        setBusy(true);
        try {
            await client.post(`/admin/orders/${order.id}/${path}`, body || {});
            toast.success(ok);
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "That didn't work");
        } finally { setBusy(false); }
    };
    const refund = () => {
        const reason = window.prompt("Reason for the refund (the customer sees this):",
                                     "Payment could not be matched to an order");
        if (reason?.trim()) act("refund", { reason: reason.trim().slice(0, 500) }, "Refunded");
    };
    const ship = (status) => {
        // There is no courier API behind this — the post receipt number, if the
        // counter gave one, is all the tracking that exists.
        let tracking = null;
        if (status === "shipped") {
            tracking = window.prompt("Post receipt number (leave blank if there isn't one):",
                                     order.tracking || "");
            if (tracking === null) return;
            tracking = tracking.trim().slice(0, 120);
        }
        act("status", { status, tracking: tracking || null }, `Customer told: ${status}`);
    };

    return (
        <div className={`card-cloud p-5 ${needsAction(order) ? "ring-2 ring-bubblegum/40" : ""}`}
            data-testid={`order-${order.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="font-display text-lg">
                        {order.book_title || "Untitled"} · ₹{(order.amount / 100).toFixed(0)}
                    </div>
                    <div className="text-xs text-plum/60 mt-1">
                        {/* Same reference the customer sees, so "about MT-BE2DE809"
                            is findable here. */}
                        <span className="font-mono text-plum/80">
                            {order.ref || order.id.slice(0, 8).toUpperCase()}
                        </span>{" · "}
                        {order.kind} · {order.method || "razorpay"} ·{" "}
                        {order.customer?.email || order.customer?.phone || "unknown customer"}
                    </div>
                    {order.upi_ref && (
                        <div className="text-xs text-plum/50 mt-1">UPI ref: <span className="font-mono">{order.upi_ref}</span></div>
                    )}
                    {order.payment_issue && order.status !== "refunded" && (
                        <div className="text-xs text-bubblegum mt-1">
                            Money missing: {order.payment_issue.reason}
                            {order.payment_issue.reference
                                ? ` · ref ${order.payment_issue.reference}` : ""}
                        </div>
                    )}
                </div>
                <Chip>{order.status}</Chip>
            </div>

            {(order.status === "upi_claimed" || order.status === "awaiting_upi"
              || order.status === "created"
              || (order.payment_issue && order.status !== "refunded")) && (
                <div className="flex flex-wrap gap-2 mt-4">
                    {order.status === "upi_claimed" && (
                        <>
                            <Button size="sm" disabled={busy} data-testid="upi-confirm-btn"
                                className="rounded-full bg-bubblegum hover:bg-bubblegum/90"
                                onClick={() => act("upi-confirm", null, "Payment confirmed")}>
                                Money's in — confirm
                            </Button>
                            <Button size="sm" variant="ghost" disabled={busy} data-testid="upi-reject-btn"
                                onClick={() => act("upi-reject", null, "Sent back to the customer")}>
                                Can't find it
                            </Button>
                        </>
                    )}
                    {order.status === "awaiting_upi" && (
                        <span className="text-xs text-plum/50 self-center">Waiting for the customer to pay.</span>
                    )}
                    {order.payment_issue && order.status !== "refunded" && (
                        <Button size="sm" variant="ghost" disabled={busy} data-testid="refund-btn"
                            onClick={refund}>
                            Send back ₹{(order.amount / 100).toFixed(0)}
                        </Button>
                    )}
                    {!order.paid_at && (
                        <Button size="sm" variant="ghost" disabled={busy} data-testid="cancel-order-btn"
                            className="text-plum/50"
                            onClick={() => window.confirm("Cancel this unpaid order?")
                                && act("cancel", null, "Order cancelled")}>
                            Cancel
                        </Button>
                    )}
                </div>
            )}

            {order.kind === "hardcover" && order.paid_at && order.status !== "refunded" && (
                <div className="mt-4 border-t border-plum/10 pt-4" data-testid="ship-panel">
                    {order.shipping && (
                        <div className="text-xs text-plum/60 mb-3 leading-relaxed">
                            {order.shipping.name}
                            {order.shipping.phone ? ` · ${order.shipping.phone}` : ""}<br />
                            {order.shipping.line1}, {order.shipping.city} — {order.shipping.pin}
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        {SHIP_FLOW.map((s) => (
                            <Button key={s} size="sm" disabled={busy || order.status === s}
                                variant={order.status === s ? "default" : "outline"}
                                className="rounded-full capitalize"
                                data-testid={`ship-${s}-btn`} onClick={() => ship(s)}>
                                {s}
                            </Button>
                        ))}
                    </div>
                    {order.tracking && (
                        <div className="text-xs text-plum/50 mt-2">
                            Post receipt: <span className="font-mono">{order.tracking}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/** Shared by all three paged lists. Hidden entirely when there is one page. */
function Pager({ meta, page, onPage, noun }) {
    const pages = meta?.pages || 1;
    const total = meta?.total ?? 0;
    if (pages <= 1) return null;
    return (
        <div className="flex items-center justify-between gap-4 mt-6" data-testid="admin-pager">
            <button onClick={() => onPage(page - 1)} disabled={page <= 1}
                data-testid="admin-page-prev"
                className="rounded-full px-4 h-9 text-sm font-medium border border-plum/15 text-plum
                           disabled:opacity-40 hover:bg-plum/5 flex items-center gap-1">
                <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <div className="text-xs text-plum/60" data-testid="admin-page-label">
                Page {page} of {pages} · {total} {noun}
            </div>
            <button onClick={() => onPage(page + 1)} disabled={page >= pages}
                data-testid="admin-page-next"
                className="rounded-full px-4 h-9 text-sm font-medium border border-plum/15 text-plum
                           disabled:opacity-40 hover:bg-plum/5 flex items-center gap-1">
                Next <ChevronRight className="w-4 h-4" />
            </button>
        </div>
    );
}

function Stat({ label, value, icon: Icon }) {
    return (
        <div className="card-cloud p-5">
            <div className="flex items-center gap-2 text-plum/50 text-xs uppercase tracking-wider mb-2">
                <Icon className="w-3.5 h-3.5" /> {label}
            </div>
            <div className="font-display text-3xl">{value ?? "—"}</div>
        </div>
    );
}

function QueueCard({ book, onDone }) {
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);
    const [title, setTitle] = useState(book.title || "");
    const fileRef = useRef(null);
    const people = [book.child, ...(book.cast || [])].filter(Boolean);

    const copyPrompt = async () => {
        try {
            await navigator.clipboard.writeText(book.master_prompt);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
            toast.success("Prompt copied — paste it into ChatGPT with the photos attached.");
        } catch {
            toast.error("Copy failed. Select the prompt below manually.");
        }
    };

    // ChatGPT needs the actual photo files attached alongside the prompt.
    const downloadPhotos = () => {
        people.forEach((p, i) => {
            const a = document.createElement("a");
            a.href = p.photo_b64;
            a.download = `${(p.name || `person${i}`).toLowerCase().replace(/[^a-z0-9]/g, "")}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        });
        toast.success(`${people.length} reference photo${people.length > 1 ? "s" : ""} downloaded`);
    };

    const setStatus = async (status) => {
        setBusy(true);
        try {
            await client.post(`/admin/books/${book.id}/status`, { status });
            toast.success(`Moved to ${status.replace("_", " ")}`);
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update");
        } finally { setBusy(false); }
    };

    const deliver = async (file) => {
        if (!file) return;
        if (file.type !== "application/pdf") { toast.error("That needs to be a PDF"); return; }
        if (file.size > MAX_PDF_MB * 1024 * 1024) { toast.error(`PDFs must be under ${MAX_PDF_MB}MB`); return; }
        const badTitle = maxLen(title, 120, "Title");
        if (badTitle) { toast.error(badTitle); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("title", title);
            const { data } = await client.post(`/admin/books/${book.id}/deliver`, fd);
            toast.success(`Delivered. Customer notified by ${data.notified}.`);
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const waitedDays = book.queued_at
        ? Math.floor((Date.now() - new Date(book.queued_at)) / 86400000)
        : null;

    return (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="card-cloud p-6" data-testid={`queue-card-${book.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                <div>
                    <div className="font-display text-2xl leading-tight">
                        {book.title || `${book.child?.name || "A child"}'s story`}
                    </div>
                    <div className="text-sm text-plum/60 mt-1">
                        {book.customer?.name || "—"} · {book.customer?.email || book.customer?.phone || "no contact"}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-sm font-medium capitalize">
                        {book.order?.kind === "hardcover" ? "Hardcover ₹799" : "PDF ₹399"}
                    </div>
                    {waitedDays !== null && (
                        <div className={`text-xs mt-1 ${waitedDays >= 2 ? "text-bubblegum font-medium" : "text-plum/50"}`}>
                            waiting {waitedDays === 0 ? "today" : `${waitedDays}d`}
                        </div>
                    )}
                </div>
            </div>

            {/* Their choices, at a glance */}
            <div className="flex flex-wrap gap-2 mb-5" data-testid="book-choices">
                <Chip>{book.theme_name}</Chip>
                <Chip>{book.art_style}</Chip>
                <Chip>{book.page_count} pages</Chip>
                <Chip>{book.text_in_image ? "text in artwork" : "captions below"}</Chip>
                {book.custom_theme && <Chip>“{book.custom_theme}”</Chip>}
            </div>

            <div className="flex flex-wrap gap-3 mb-5">
                {people.map((p) => (
                    <div key={p.id} className="text-center w-20">
                        <img src={p.photo_b64} alt={p.name}
                            className="w-20 h-20 rounded-xl object-cover border border-plum/10" />
                        <div className="text-xs font-medium mt-1 truncate">{p.name}</div>
                        <div className="text-[10px] text-plum/50 capitalize truncate">
                            {p.role_label || p.role}
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
                <Button onClick={copyPrompt} className="rounded-full bg-bubblegum hover:bg-bubblegum/90"
                    data-testid="copy-prompt-btn">
                    {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    {copied ? "Copied" : "Copy ChatGPT prompt"}
                </Button>
                <Button variant="outline" className="rounded-full" onClick={downloadPhotos}
                    data-testid="download-photos-btn">
                    <Download className="w-4 h-4 mr-1" /> Photos ({people.length})
                </Button>
                {book.status === "queued" && (
                    <Button variant="ghost" className="rounded-full" disabled={busy}
                        onClick={() => setStatus("in_progress")} data-testid="start-work-btn">
                        Start working
                    </Button>
                )}
                {book.status === "in_progress" && (
                    <Button variant="ghost" className="rounded-full" disabled={busy}
                        onClick={() => setStatus("queued")}>
                        Back to queue
                    </Button>
                )}
                {book.status === "revision" && (
                    <Button variant="ghost" className="rounded-full" disabled={busy}
                        onClick={() => setStatus("in_progress")} data-testid="start-revision-btn">
                        Start the changes
                    </Button>
                )}
            </div>

            {(book.revision_notes || []).length > 0 && (
                <div className="mb-4 rounded-xl border-2 border-bubblegum/30 bg-bubblegum/5 p-4"
                    data-testid="revision-notes">
                    <div className="text-xs uppercase tracking-wider text-bubblegum mb-2">
                        What they want changed · revision {book.revisions_used} of 2
                    </div>
                    {book.revision_notes.map((r, i) => (
                        <p key={i} className="text-sm text-plum/80 leading-relaxed mb-1 last:mb-0">
                            “{r.note}”
                            <span className="text-xs text-plum/40 ml-2">
                                {r.at ? new Date(r.at).toLocaleDateString() : ""}
                            </span>
                        </p>
                    ))}
                </div>
            )}

            <details className="mb-4">
                <summary className="text-sm text-plum/60 cursor-pointer select-none hover:text-plum">
                    Show the prompt
                </summary>
                <pre className="mt-3 text-xs bg-sunlit/60 border border-plum/10 rounded-xl p-4 max-h-80 overflow-auto whitespace-pre-wrap font-mono text-plum/80"
                    data-testid="master-prompt">{book.master_prompt}</pre>
            </details>

            {book.status === "delivered" ? (
                <div className="flex items-center gap-2 text-sm text-plum/60 border-t border-plum/10 pt-4">
                    <Check className="w-4 h-4 text-bubblegum" />
                    Delivered {book.delivered_at ? new Date(book.delivered_at).toLocaleDateString() : ""}
                    {book.pdf_size ? ` · ${(book.pdf_size / 1048576).toFixed(1)} MB` : ""}
                    <label className="ml-auto text-xs underline cursor-pointer hover:text-plum">
                        Replace PDF
                        <input type="file" accept="application/pdf" className="hidden" ref={fileRef}
                            onChange={(e) => deliver(e.target.files?.[0])} />
                    </label>
                </div>
            ) : (
                <div className="border-t border-plum/10 pt-4 flex flex-wrap items-center gap-3">
                    <Input value={title} onChange={(e) => setTitle(e.target.value)}
                        placeholder="Book title (shown to the family)" maxLength={120}
                        className="bg-white flex-1 min-w-[220px]" data-testid="deliver-title-input" />
                    <label className={`btn-sticker text-sm cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}
                        data-testid="deliver-pdf-label">
                        {busy ? <Loader2 className="w-4 h-4 inline animate-spin" /> : <Upload className="w-4 h-4 inline mr-1" />}
                        {busy ? " Uploading…" : "Upload finished PDF"}
                        <input type="file" accept="application/pdf" className="hidden" ref={fileRef}
                            onChange={(e) => deliver(e.target.files?.[0])} />
                    </label>
                </div>
            )}
        </motion.div>
    );
}

function Chip({ children }) {
    return (
        <span className="text-xs rounded-full px-3 py-1.5 bg-lavender/60 text-plum capitalize">
            {children}
        </span>
    );
}

function SamplesManager() {
    const [samples, setSamples] = useState([]);
    const [caption, setCaption] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        client.get("/samples").then(({ data }) => setSamples(data.samples || [])).catch(() => {});
    }, []);
    useEffect(load, [load]);

    const upload = async (files) => {
        if (!files?.length) return;
        const bad = firstError([
            maxLen(caption, 120, "Caption"),
            ...Array.from(files).map(imageFile),
        ]);
        if (bad) { toast.error(bad); return; }
        setBusy(true);
        try {
            for (let i = 0; i < files.length; i++) {
                const fd = new FormData();
                fd.append("file", files[i]);
                fd.append("caption", caption);
                fd.append("order", String(samples.length + i));
                await client.post("/admin/samples", fd);
            }
            toast.success(`${files.length} sample${files.length > 1 ? "s" : ""} added`);
            setCaption("");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally { setBusy(false); }
    };

    const remove = async (id) => {
        await client.delete(`/admin/samples/${id}`).catch(() => {});
        load();
    };

    return (
        <div>
            <div className="card-cloud p-6 mb-6">
                <div className="font-display text-xl mb-1">Sample pages</div>
                <p className="text-sm text-plum/60 mb-4">
                    These show on <span className="font-mono text-xs">/samples</span> to visitors who
                    haven't signed in. Upload the pages you're happy to show publicly.
                </p>
                <div className="flex flex-wrap gap-3 items-center">
                    <Input value={caption} onChange={(e) => setCaption(e.target.value)}
                        placeholder="Caption (optional)" maxLength={120} className="bg-white flex-1 min-w-[220px]"
                        data-testid="sample-caption-input" />
                    <label className={`btn-sticker text-sm cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}
                        data-testid="sample-upload-label">
                        {busy ? <Loader2 className="w-4 h-4 inline animate-spin" /> : <Upload className="w-4 h-4 inline mr-1" />}
                        {busy ? " Uploading…" : "Add images"}
                        <input type="file" accept="image/*" multiple className="hidden"
                            onChange={(e) => upload(e.target.files)} />
                    </label>
                </div>
            </div>

            {samples.length === 0 ? (
                <div className="card-cloud p-12 text-center text-plum/60">
                    No samples yet — the public page shows a friendly placeholder until you add some.
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-samples-grid">
                    {samples.map((s) => (
                        <div key={s.id} className="card-cloud overflow-hidden relative group">
                            <img src={mediaUrl(s.image_url)} alt={s.caption} className="w-full aspect-square object-cover" />
                            {s.caption && <div className="p-3 text-xs text-plum/70">{s.caption}</div>}
                            <button onClick={() => remove(s.id)} aria-label="Delete sample"
                                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <Trash2 className="w-4 h-4 text-bubblegum" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
